/**
 * `<AppShell>` — the shell layout + the `ShellMount` host (frontend.md §2.1).
 *
 * This is the frontend workstream's contract surface to Agent 9. It:
 *  1. Builds the single `ShellMount` registry (`createShellMount`) and provides it via
 *     context, so Agent 9's editor/chat can register routes/nav/commands/status-bar slots.
 *  2. Seeds the registry with the frontend's own seven core surfaces (`seedCoreSurfaces`).
 *  3. Renders the chrome — `<NavSidebar>`, `<CommandPalette>`, `<GlobalStatusBar>` — and
 *     the `<RouteOutlet>` where the active surface (core or Agent-9) renders.
 *  4. Syncs `useUiStore.activeSurface` to the current route.
 *
 * The `ShellMount` is built once (a `useRef`) so its identity is stable across renders —
 * Agent 9's registration effects depend on that stability.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@/components';
import { useUiStore } from '@/stores';
import { surfaceForPath } from '@/routes';
// Subscribe to the backend SSE change-event stream once the shell is alive — every
// view that reads from TanStack Query auto-refetches when MCP or another tab writes
// to the backend. (friction-log fix #92, 2026-05-29).
import { useChangeEventStream } from '@/hooks/useChangeEventStream';
import { createShellMount } from './createShellMount';
import { ShellMountProvider, bindRegistryVersionCounter } from './ShellMountContext';
import { seedCoreSurfaces } from './surfaceRegistry';
import { CommandPalette } from './CommandPalette';
import { GlobalStatusBar } from './GlobalStatusBar';
import { RouteOutlet } from './RouteOutlet';
import { TopBar } from './TopBar';
import { KeyboardShortcuts } from './KeyboardShortcuts';
import { KeyboardHelp } from './KeyboardHelp';
// Notebook tree is now a top-level shell pane — always visible on the left,
// regardless of which surface is active. Editor / Search / Chat all share
// the same notebook navigation. Replaces the old <NavSidebar /> (the
// surface tab nav moved to the TopBar).
import { NotebooksTreePane } from '@/editor/NotebooksTreePane';
import type { NotebookId } from '@/types';
import { EditorChatMount } from '@/editor';
import type { ShellMount } from './ShellMount';
// Phase D (2026-05-19) — desktop wrapper class hook. The window controls
// now live INSIDE <TopBar> (unified chrome bar — no more separate
// DesktopTitleBar above it), so this just toggles the .app-shell--desktop
// class for the border + drag-region CSS.
import { isDesktopApp, isMacDesktop } from '@/desktop/isDesktopApp';
import { WindowResizeGrips } from '@/desktop/WindowResizeGrips';

export function AppShell(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const setActiveSurface = useUiStore((s) => s.setActiveSurface);
  // Notebooks-pane collapse (reading/presentation mode) — mirrors the editor's
  // details-pane edge tab, on the left edge.
  const notebooksCollapsed = useUiStore((s) => s.notebooksCollapsed);
  const setNotebooksCollapsed = useUiStore((s) => s.setNotebooksCollapsed);
  // Open the SSE change-event stream on shell mount. Hook is a no-op until a token is
  // available (Clerk signin pending / dev without auth) and reconnects on disconnect.
  useChangeEventStream();

  // Build the ShellMount exactly once — its identity must be stable (Agent 9's
  // registration effects depend on it).
  const mountRef = useRef<ShellMount | null>(null);
  if (mountRef.current === null) {
    const mount = createShellMount();
    bindRegistryVersionCounter(mount);
    mountRef.current = mount;
  }
  const mount = mountRef.current;

  // Seed the seven core surfaces into the registry once. Agent 9 registers /editor and
  // /chat through the same `mount` from its own entry points (frontend.md §3.2).
  useEffect(() => {
    const teardown = seedCoreSurfaces(mount, navigate);
    return teardown;
  }, [mount, navigate]);

  // Keep `useUiStore.activeSurface` in sync with the route (frontend.md §2.2). Also
  // writes `data-screen-label` on <html> per the standalone (UX-reconciliation F3 —
  // DECISIONS.md §1.1) so per-surface CSS can target the current surface.
  useEffect(() => {
    const surface = surfaceForPath(location.pathname);
    setActiveSurface(surface);
    document.documentElement.setAttribute('data-screen-label', surface);
  }, [location.pathname, setActiveSurface]);

  return (
    <ShellMountProvider value={mount}>
      {/* Agent 9's editor-chat workstream registers /editor and /chat into the same `mount`
          from its own entry point — exactly as this file's header anticipates ("Agent 9
          registers ... through the same `mount` from its own entry points"). It renders
          nothing; it is a registration effect. */}
      <EditorChatMount />
      {/* UX-reconciliation F3 — the global single-letter shortcut handler. Pure effect;
          reads the kbd map from the same ShellMount registry NavSidebar reads. */}
      <KeyboardShortcuts />
      <div
        className={
          'app-shell app-shell--with-topbar app-shell--notebooks-left' +
          (notebooksCollapsed ? ' app-shell--notebooks-collapsed' : '') +
          (isDesktopApp() ? ' app-shell--desktop' : '')
        }
      >
        {/* Unified TopBar — owns surface tabs, search, user button, AND
            the desktop window controls + drag region (when isDesktopApp).
            No separate title bar; one chrome row. */}
        <TopBar />
        <NotebooksShellPane />
        {/* Collapse/expand toggle for the notebooks pane — the left-edge twin of the
            editor's details-pane tab. Sits at the pane's right border when expanded
            (‹, collapse) and at the far left edge when collapsed (›, expand). */}
        <button
          type="button"
          className="nb-toggle"
          onClick={() => setNotebooksCollapsed(!notebooksCollapsed)}
          title={notebooksCollapsed ? 'Show notebooks panel' : 'Hide notebooks panel'}
          aria-label={notebooksCollapsed ? 'Show notebooks panel' : 'Hide notebooks panel'}
          aria-expanded={!notebooksCollapsed}
        >
          <Icon name="chevron_right" size={14} />
        </button>
        <div className="app-shell__main">
          <RouteOutlet />
        </div>
        <GlobalStatusBar />
        <CommandPalette />
        {/* U8 — the `?` shortcut-reference overlay. Reads the same ShellMount nav
            registry KeyboardShortcuts navigates from; registers its own palette
            command ("Keyboard shortcuts"). */}
        <KeyboardHelp />
        {/* Desktop-only: custom edge/corner grips for OS resize, since the FRAMELESS
            (Windows/Linux) window has no native resize border. macOS keeps native window
            decorations (titleBarStyle: Overlay) so it already has native resize edges —
            skip the custom grips there. */}
        {isDesktopApp() && !isMacDesktop() && <WindowResizeGrips />}
      </div>
    </ShellMountProvider>
  );
}

/**
 * `<NotebooksShellPane>` — thin wrapper around `<NotebooksTreePane />` that
 * reads the active notebook + note from the URL so the tree highlights the
 * right rows regardless of which surface is currently mounted. When the
 * user is on /search or /chat, neither `noteId` (URL param) nor
 * `notebookId` (search param) is set, so the tree renders with no
 * highlighted row — which is correct.
 */
function NotebooksShellPane(): JSX.Element {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // 2026-05-18 — must use `useMatch`, NOT `useParams`. NotebooksShellPane
  // is a SIBLING of RouteOutlet (the component that actually renders
  // `<Routes><Route path="/editor/:noteId" .../></Routes>`), not a
  // descendant. `useParams` only sees params from the nearest matched
  // <Route> ancestor — since there isn't one above NotebooksShellPane,
  // it returned an empty object and `noteId` was always `undefined`.
  // Result: activeNoteId was always `null`, NotebooksTreePane's pulse
  // effect always hit its `activeNoteId === null` early-return, the
  // entire orange-glow / scroll-to-row / auto-expand chain was dead.
  //
  // `useMatch` works anywhere — it matches the current pathname against
  // the supplied pattern and returns the parsed params (or null on no
  // match). Pure pathname → params, no router ancestor required.
  const editorMatch = useMatch('/editor/:noteId');
  const noteId = editorMatch?.params.noteId ?? null;
  // Only treat the URL params as "active" when actually on the editor route.
  // Other surfaces don't carry a noteId in the path but might have
  // unrelated ?notebookId= chrome we shouldn't mis-highlight from.
  const isEditorRoute = location.pathname.startsWith('/editor');
  const activeNoteId = isEditorRoute ? noteId : null;
  const activeNotebookId = isEditorRoute
    ? ((searchParams.get('notebookId') as NotebookId | null) ?? null)
    : null;
  return (
    <NotebooksTreePane
      activeNoteId={activeNoteId}
      activeNotebookId={activeNotebookId}
    />
  );
}
